import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageCircle, RefreshCw, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, apiError } from "@/lib/api";

const RESEND_SECONDS = 45;

export default function WhatsAppOtpDialog({ open, phone, email, onClose, onVerified }) {
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const started = useRef(false);

  const send = async () => {
    setSending(true);
    setError("");
    try {
      const { data } = await api.post("/vendor/otp/send", { phone, email });
      setSent(data);
      setCooldown(RESEND_SECONDS);
      toast.success(`Code envoyé par email à ${data.masked}`);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (open && !started.current) { started.current = true; send(); }
    if (!open) { started.current = false; setCode(""); setSent(null); setError(""); setCooldown(0); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = async (value) => {
    const c = value ?? code;
    if (c.length !== 6) return;
    setVerifying(true);
    setError("");
    try {
      const { data } = await api.post("/vendor/otp/verify", { phone, code: c });
      onVerified(data.phone_verification_token, data.phone);
    } catch (err) {
      setError(apiError(err));
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-md" data-testid="whatsapp-otp-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-lg font-bold text-ink">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><MessageCircle className="h-4 w-4" /></span>
            Vérification par email
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          Un code à 6 chiffres a été envoyé par email à <span className="font-bold text-ink" data-testid="otp-phone">{sent?.masked || email}</span>.
          Saisissez-le pour confirmer votre adresse et créer votre compte revendeur. Il est valable 10 minutes.
        </p>
        <div className="flex justify-center py-3">
          <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }} data-testid="otp-input" disabled={verifying || sending}>
            <InputOTPGroup className="gap-2">
              {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 rounded-xl border text-lg font-black" />)}
            </InputOTPGroup>
          </InputOTP>
        </div>
        {error && <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="otp-error">{error}</div>}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button type="button" onClick={send} disabled={sending || cooldown > 0} data-testid="otp-resend"
            className="flex items-center gap-1.5 text-xs font-bold text-brand disabled:text-slate-400">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {cooldown > 0 ? `Renvoyer le code (${cooldown}s)` : "Renvoyer le code"}
          </button>
          <button type="button" onClick={() => verify()} disabled={code.length !== 6 || verifying} data-testid="otp-verify"
            className="flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">
            {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Vérifier
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
